import { useEffect, useRef, useState } from "react";
import {
  ActionList, ActionMenu, Checkbox, Dialog, FormControl, Heading, Label, Select, Text, TextInput
} from "@primer/react";
import { CopyIcon, DownloadIcon, FileIcon, LinkIcon, PencilIcon, PlusIcon, ShareIcon, SyncIcon, TrashIcon, UploadIcon, XCircleIcon } from "@primer/octicons-react";
import { Alert } from "../Alert/Alert.jsx";
import { Button } from "../Button/Button.jsx";
import { PageHeading } from "../PageHeading/PageHeading.jsx";
import { ProfileIdentity } from "../ProfileIdentity/ProfileIdentity.jsx";
import { policyDisplayTitle } from "../PolicyIdentity/PolicyIdentity.jsx";
import { OpenSharedLinkDialog } from "../SharedProfile/OpenSharedLinkDialog.jsx";
import { SharedProfileStatus } from "../SharedProfile/SharedProfile.jsx";
import { ProfileSyncDialog } from "./ProfileSyncDialog.jsx";
import {
  createProfileShare, parseProfileShare, profileDataForSections,
  profileShareSummary, uniqueImportedProfileName
} from "../../domain/profileShare.js";
import { prepareProfileSectionsCopy, profileDataWithSectionsReplaced } from "../../domain/applicationDocument.js";
import { saveSharedProfile } from "../../lib/sharedProfileApi.js";
import { sharedProfileLink } from "../../lib/sharedProfileLink.js";
import { createShareToken } from "../../lib/shareToken.js";
import { readPresentationState, writePresentationState } from "../../storage/presentationState.js";
import { sharedProfileLastSavedAt, sharedProfileToken } from "../../storage/profileSharingPreferences.js";
import "./ProfileManager.css";
import { sortProfilesByName } from "../../lib/sortProfilesByName.js";

const allProfileSections = { configuration: true, allocation: true, browserViews: true };
const sharedCopyLifetime = 7 * 24 * 60 * 60 * 1_000;
const sharedCopyRequests = new Map();

export function ProfileManager({ application, applicationError, fetchRequest = fetch, onSyncConnectionChange }) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmClearId, setConfirmClearId] = useState(null);
  const [name, setName] = useState("");
  const [sourceProfileId, setSourceProfileId] = useState(application.profile?.id ?? "");
  const [sections, setSections] = useState({ configuration: true, allocation: false, browserViews: true });
  const [exportingProfile, setExportingProfile] = useState(null);
  const [exportSections, setExportSections] = useState({ configuration: true, allocation: false, browserViews: true });
  const [importDraft, setImportDraft] = useState(null);
  const [importSections, setImportSections] = useState(null);
  const [importName, setImportName] = useState("");
  const [importTargetId, setImportTargetId] = useState("");
  const [confirmingImportReplace, setConfirmingImportReplace] = useState(false);
  const [copyTarget, setCopyTarget] = useState(null);
  const [copySourceId, setCopySourceId] = useState("");
  const [copySections, setCopySections] = useState({ configuration: true, allocation: false, browserViews: true, keepLocalPolicySettings: false });
  const [confirmingCopy, setConfirmingCopy] = useState(false);
  const [sharingProfile, setSharingProfile] = useState(null);
  const [syncingProfile, setSyncingProfile] = useState(null);
  const [openingSharedLink, setOpeningSharedLink] = useState(false);
  const [, setSyncRevision] = useState(0);
  const [error, setError] = useState(null);
  const importInput = useRef(null);
  const destructiveReturnFocusRef = useRef(null);
  const renameReturnFocusRef = useRef(null);
  const shareReturnFocusRef = useRef(null);
  const syncReturnFocusRef = useRef(null);
  const sharedLinkReturnFocusRef = useRef(null);

  function beginCreate() {
    setCreating(true);
    setEditingId(null);
    setConfirmDeleteId(null);
    setConfirmClearId(null);
    setName("");
    setSourceProfileId(application.profile?.id ?? "");
    setSections({ configuration: true, allocation: false, browserViews: true });
    setError(null);
  }

  function beginRename(profile) {
    renameReturnFocusRef.current = document.getElementById(profileActionsId(profile.id));
    setCreating(false);
    setConfirmDeleteId(null);
    setConfirmClearId(null);
    setEditingId(profile.id);
    setName(profile.name);
    setError(null);
  }

  function beginShare(profile) {
    shareReturnFocusRef.current = document.getElementById(profileActionsId(profile.id));
    setSharingProfile({ cachedLink: cachedSharedCopyLink(profile), profile });
    setError(null);
  }

  function beginClear(profile) {
    destructiveReturnFocusRef.current = document.getElementById(profileActionsId(profile.id));
    setConfirmClearId(profile.id);
    setConfirmDeleteId(null);
    setError(null);
  }

  function beginDelete(profile) {
    destructiveReturnFocusRef.current = document.getElementById(profileActionsId(profile.id));
    setConfirmDeleteId(profile.id);
    setConfirmClearId(null);
    setError(null);
  }

  function beginSync(profile) {
    syncReturnFocusRef.current = document.getElementById(profileActionsId(profile.id));
    setSyncingProfile(profile);
    setError(null);
  }

  function beginOpenSharedLink(returnFocusTarget) {
    sharedLinkReturnFocusRef.current = returnFocusTarget;
    setSyncingProfile(null);
    setOpeningSharedLink(true);
    setError(null);
  }

  function syncConnectionChanged() {
    setSyncRevision((revision) => revision + 1);
    onSyncConnectionChange?.();
  }

  function closeDestructiveDialog() {
    setConfirmDeleteId(null);
    setConfirmClearId(null);
    setError(null);
  }

  function closeRename() {
    setEditingId(null);
    setName("");
    setError(null);
  }

  async function create() {
    const validationError = profileNameError(application.profiles, name);
    if (validationError) {
      setError(validationError);
      return;
    }
    const saved = await application.createProfile({
      name,
      sourceProfileId: sourceProfileId || null,
      sections: sourceProfileId ? sections : {}
    });
    if (saved) {
      setCreating(false);
      setName("");
    } else {
      setError("The profile could not be created.");
    }
  }

  async function rename() {
    const validationError = profileNameError(application.profiles, name, editingId);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (await application.renameProfile(editingId, name)) {
      closeRename();
    } else {
      setError("The profile could not be renamed.");
    }
  }

  async function remove(profileId) {
    const nextFocusProfileId = profileIdAfterDeletion(application, profileId);
    if (await application.deleteProfile(profileId)) {
      setConfirmDeleteId(null);
      setError(null);
      if (nextFocusProfileId) requestAnimationFrame(() => document.getElementById(profileActionsId(nextFocusProfileId))?.focus());
    } else {
      setError("The profile could not be deleted.");
    }
  }

  async function clearData(profileId) {
    if (await application.clearProfileData(profileId)) {
      setConfirmClearId(null);
      setError(null);
    } else {
      setError("The profile data could not be cleared.");
    }
  }

  async function selectProfile(profileId) {
    setError(null);
    if (!await application.switchProfile(profileId)) {
      setError("The profile could not be opened.");
    }
  }

  function changeSection(section, checked) {
    setSections((current) => changeSectionSelection(current, section, checked));
  }

  function beginExport(profile) {
    setExportingProfile(profile);
    setExportSections({ configuration: true, allocation: false, browserViews: true });
    setError(null);
  }

  function beginCopy(profile) {
    const source = application.profile?.id !== profile.id
      ? application.profile
      : application.profiles.find((candidate) => candidate.id !== profile.id);
    setCreating(false);
    setEditingId(null);
    setConfirmDeleteId(null);
    setConfirmClearId(null);
    setCopyTarget(profile);
    setCopySourceId(source?.id ?? "");
    setCopySections({ configuration: true, allocation: false, browserViews: true, keepLocalPolicySettings: false });
    setConfirmingCopy(false);
    setError(null);
  }

  function exportProfile() {
    try {
      const share = createProfileShare(exportingProfile, exportSections);
      downloadProfileShare(share);
      setExportingProfile(null);
    } catch (exportError) {
      setError(exportError.message);
    }
  }

  async function readImportFile(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const share = parseProfileShare(JSON.parse(await file.text()));
      const matchingProfile = application.profiles.find((profile) => profile.name.trim().toLocaleLowerCase() === share.profile.name.trim().toLocaleLowerCase());
      setImportDraft(share);
      setImportSections(share.profile.sections);
      setImportName(uniqueImportedProfileName(application.profiles, share.profile.name));
      setImportTargetId(matchingProfile?.id ?? "");
      setConfirmingImportReplace(false);
      setError(null);
    } catch (importError) {
      setImportDraft(null);
      setError(`Could not import profile: ${importError.message}`);
    }
  }

  async function importProfile() {
    const validationError = profileNameError(application.profiles, importName);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const data = profileDataForSections(importDraft, importSections);
      if (await application.importProfile({ name: importName, data })) {
        setImportDraft(null);
        setImportSections(null);
        setImportName("");
      } else {
        setError("The profile could not be imported.");
      }
    } catch (importError) {
      setError(importError.message);
    }
  }

  function reviewImportReplacement() {
    const target = application.profiles.find((profile) => profile.id === importTargetId);
    if (!target) {
      setError("Choose a profile to replace.");
      return;
    }
    if (!selectedSectionNames(importSections).length) {
      setError("Select at least one section to replace.");
      return;
    }
    try {
      profileDataWithSectionsReplaced(target.data, importDraft.profile.data, importSections);
      setConfirmingImportReplace(true);
      setError(null);
    } catch (replacementError) {
      setError(`Cannot replace ${target.name}: ${replacementError.message}`);
    }
  }

  async function replaceImportedProfile() {
    const target = application.profiles.find((profile) => profile.id === importTargetId);
    if (!target) return;
    try {
      const data = profileDataWithSectionsReplaced(target.data, importDraft.profile.data, importSections);
      if (await application.replaceProfileData(target.id, data)) closeImport();
      else setError(`The ${target.name} profile could not be replaced.`);
    } catch (replacementError) {
      setConfirmingImportReplace(false);
      setError(`Cannot replace ${target.name}: ${replacementError.message}`);
    }
  }

  function closeImport() {
    setImportDraft(null);
    setImportSections(null);
    setImportName("");
    setImportTargetId("");
    setConfirmingImportReplace(false);
    setError(null);
  }

  function reviewProfileCopy() {
    const source = application.profiles.find((profile) => profile.id === copySourceId);
    if (!source || !copyTarget) {
      setError("Choose a source profile.");
      return;
    }
    if (!selectedSectionNames(copySections).length) {
      setError("Select at least one section to copy.");
      return;
    }
    try {
      prepareProfileSectionsCopy(copyTarget.data, source.data, copySections);
      setConfirmingCopy(true);
      setError(null);
    } catch (copyError) {
      setError(`Cannot update ${copyTarget.name}: ${copyError.message}`);
    }
  }

  async function copyProfileSections() {
    const source = application.profiles.find((profile) => profile.id === copySourceId);
    if (!source || !copyTarget) return;
    try {
      const data = profileDataWithSectionsReplaced(copyTarget.data, source.data, copySections);
      if (await application.replaceProfileData(copyTarget.id, data)) closeProfileCopy();
      else setError(`The ${copyTarget.name} profile could not be updated.`);
    } catch (copyError) {
      setConfirmingCopy(false);
      setError(`Cannot update ${copyTarget.name}: ${copyError.message}`);
    }
  }

  function closeProfileCopy() {
    setCopyTarget(null);
    setCopySourceId("");
    setConfirmingCopy(false);
    setError(null);
  }

  const activeProfile = application.profile;
  const otherProfiles = sortProfilesByName(application.profiles.filter((profile) => profile.id !== activeProfile.id));
  const pageError = profileManagerPageError(error, [editingId, confirmDeleteId, confirmClearId]);

  return <div className="profile-manager">
    <PageHeading
      actions={<><Button as="a" href="#/reports/new" tone="secondary" leadingVisual={FileIcon}>Create report</Button><ActionMenu><ActionMenu.Button id="profile-import-actions" disabled={creating}>Import</ActionMenu.Button><ActionMenu.Overlay><ActionList>
        <ActionList.Item onSelect={() => importInput.current?.click()}><ActionList.LeadingVisual><UploadIcon /></ActionList.LeadingVisual>Import from file…</ActionList.Item>
        <ActionList.Item onSelect={() => beginOpenSharedLink(document.getElementById("profile-import-actions"))}><ActionList.LeadingVisual><LinkIcon /></ActionList.LeadingVisual>Open shared link…</ActionList.Item>
      </ActionList></ActionMenu.Overlay></ActionMenu><Button leadingVisual={PlusIcon} onClick={beginCreate} disabled={creating}>New profile</Button></>}
      description="Manage isolated portfolios, configurations, and prepared views."
    >Profiles</PageHeading>
    {applicationError ? <Alert tone="danger" heading="Changes were not saved">{applicationError.message}</Alert> : null}
    {pageError ? <Alert tone="danger" heading="Unable to update profiles">{pageError}</Alert> : null}
    <ActiveProfile
      application={application}
      onClear={() => beginClear(activeProfile)}
      onCopy={() => beginCopy(activeProfile)}
      onDelete={() => beginDelete(activeProfile)}
      onExport={() => beginExport(activeProfile)}
      onRename={() => beginRename(activeProfile)}
      onShare={() => beginShare(activeProfile)}
      onSync={() => beginSync(activeProfile)}
      profile={activeProfile}
    />

    {otherProfiles.length ? <div className="profile-list">
      {otherProfiles.map((profile) => <div className="profile-row selectable" key={profile.id}>
        <button className="profile-switch" type="button" aria-label={`Switch to ${profile.name}`} onClick={() => selectProfile(profile.id)} disabled={application.saving}><ProfileIdentity profile={profile} /></button>
        <InactiveProfileActions onDelete={() => beginDelete(profile)} onRename={() => beginRename(profile)} profile={profile} />
      </div>)}
    </div> : null}

    <input ref={importInput} className="profile-import-input" type="file" accept=".json,application/json" onChange={readImportFile} />

        {creating ? <div className="profile-creator">
      <div className="profile-creator-fields">
        <FormControl required>
          <FormControl.Label>Profile name</FormControl.Label>
          <TextInput value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Prepared portfolio" block autoFocus />
        </FormControl>
        <FormControl>
          <FormControl.Label>Start with</FormControl.Label>
          <Select value={sourceProfileId} onChange={(event) => setSourceProfileId(event.target.value)} block>
            <Select.Option value="">Blank profile</Select.Option>
            {application.profiles.map((profile) => <Select.Option value={profile.id} key={profile.id}>Copy from {profile.name}</Select.Option>)}
          </Select>
        </FormControl>
      </div>
      {sourceProfileId ? <fieldset className="profile-sections">
        <legend>Copy sections</legend>
        <FormControl layout="horizontal" disabled={sections.allocation || sections.browserViews}><Checkbox checked={sections.configuration} onChange={(event) => changeSection("configuration", event.target.checked)} /><FormControl.Label>Configuration</FormControl.Label><FormControl.Caption>Properties, assets, assignments, and policies</FormControl.Caption></FormControl>
        <FormControl layout="horizontal"><Checkbox checked={sections.browserViews} onChange={(event) => changeSection("browserViews", event.target.checked)} /><FormControl.Label>Browser views</FormControl.Label></FormControl>
        <FormControl layout="horizontal"><Checkbox checked={sections.allocation} onChange={(event) => changeSection("allocation", event.target.checked)} /><FormControl.Label>Allocation</FormControl.Label></FormControl>
      </fieldset> : null}
      <div className="profile-creator-actions"><Button tone="quiet" onClick={() => setCreating(false)}>Cancel</Button><Button onClick={create} disabled={application.saving}>Create profile</Button></div>
    </div> : null}

    {exportingProfile ? <ExportProfileDialog error={error} profile={exportingProfile} sections={exportSections} onChange={(section, checked) => setExportSections((current) => changeSectionSelection(current, section, checked))} onClose={() => { setExportingProfile(null); setError(null); }} onExport={exportProfile} /> : null}
    {sharingProfile ? <ShareProfileDialog cachedLink={sharingProfile.cachedLink} fetchRequest={fetchRequest} onClose={() => setSharingProfile(null)} profile={sharingProfile.profile} returnFocusRef={shareReturnFocusRef} /> : null}
    {editingId ? <RenameProfileDialog application={application} error={error} name={name} onChange={setName} onClose={closeRename} onRename={rename} profile={application.profiles.find((profile) => profile.id === editingId)} returnFocusRef={renameReturnFocusRef} /> : null}
    {confirmClearId ? <DestructiveProfileDialog action="clear" application={application} error={error} onClose={closeDestructiveDialog} onConfirm={() => clearData(confirmClearId)} profile={application.profiles.find((profile) => profile.id === confirmClearId)} returnFocusRef={destructiveReturnFocusRef} /> : null}
    {confirmDeleteId ? <DestructiveProfileDialog action="delete" application={application} error={error} onClose={closeDestructiveDialog} onConfirm={() => remove(confirmDeleteId)} profile={application.profiles.find((profile) => profile.id === confirmDeleteId)} returnFocusRef={destructiveReturnFocusRef} /> : null}
    <ProfileSyncDialog fetchRequest={fetchRequest} onClose={() => setSyncingProfile(null)} onConnectionChange={syncConnectionChanged} onOpenSharedLink={() => beginOpenSharedLink(syncReturnFocusRef.current)} profile={syncingProfile} returnFocusRef={syncReturnFocusRef} />
    {openingSharedLink ? <OpenSharedLinkDialog onClose={() => setOpeningSharedLink(false)} returnFocusRef={sharedLinkReturnFocusRef} /> : null}
    {importDraft ? <ImportProfileDialog application={application} confirmingReplace={confirmingImportReplace} error={error} name={importName} onBack={() => setConfirmingImportReplace(false)} onChangeName={setImportName} onChangeSection={(section, checked) => { setImportSections((current) => changeSectionSelection(current, section, checked)); setConfirmingImportReplace(false); setError(null); }} onChangeTarget={(profileId) => { setImportTargetId(profileId); setConfirmingImportReplace(false); setError(null); }} onClose={closeImport} onImport={importTargetId ? reviewImportReplacement : importProfile} onReplace={replaceImportedProfile} sections={importSections} share={importDraft} targetId={importTargetId} /> : null}
    {copyTarget ? <CopyProfileSectionsDialog application={application} confirming={confirmingCopy} error={error} onBack={() => setConfirmingCopy(false)} onChangeSection={(section, checked) => { setCopySections((current) => changeSectionSelection(current, section, checked)); setConfirmingCopy(false); setError(null); }} onChangeSource={(profileId) => { setCopySourceId(profileId); setConfirmingCopy(false); setError(null); }} onClose={closeProfileCopy} onConfirm={copyProfileSections} onReview={reviewProfileCopy} sections={copySections} sourceId={copySourceId} target={copyTarget} /> : null}
  </div>;
}

function ShareProfileDialog({ cachedLink, fetchRequest, onClose, profile, returnFocusRef }) {
  const [link, setLink] = useState(cachedLink);
  const [status, setStatus] = useState(cachedLink ? { tone: "success", text: "Share link ready." } : null);

  useEffect(() => {
    if (cachedLink) return undefined;
    let active = true;
    requestSharedCopy(fetchRequest, profile)
      .then((nextLink) => {
        if (active) {
          writePresentationState(sharedCopyStateOptions(profile.id), { createdAt: new Date().toISOString(), link: nextLink, profileUpdatedAt: profile.updatedAt });
          setLink(nextLink);
          setStatus({ tone: "success", text: "Share link ready." });
        }
      })
      .catch((creationError) => {
        if (active) setStatus({ tone: "danger", text: creationError.message });
      });
    return () => { active = false; };
  }, [cachedLink, fetchRequest, profile]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      setStatus({ tone: "success", text: "Share link copied." });
    } catch {
      setStatus({ tone: "danger", text: "The share link could not be copied." });
    }
  }

  return <Dialog
    className="profile-copy-dialog"
    title={`Share a copy of ${profile.name}`}
    onClose={onClose}
    returnFocusRef={returnFocusRef}
  >
    <div className="profile-copy-dialog-body">
      <Text size="medium" weight="normal">This creates a temporary server copy. Anyone with the link can import it as a separate profile; future changes will not stay connected.</Text>
    </div>
    <Dialog.Footer className="profile-copy-footer">
      <div className="profile-copy-footer-status">{status ? <SharedProfileStatus status={status} /> : <Text className="muted-text" role="status">Creating share link…</Text>}</div>
      <div className="profile-copy-footer-actions"><Button tone="secondary" onClick={onClose}>Close</Button><Button onClick={copyLink} disabled={!link}>Copy link</Button></div>
    </Dialog.Footer>
  </Dialog>;
}

function validCachedShare(value) {
  return value
    && typeof value.createdAt === "string"
    && typeof value.link === "string"
    && typeof value.profileUpdatedAt === "string"
    ? value
    : null;
}

function requestSharedCopy(fetchRequest, profile) {
  const requestKey = `${profile.id}:${profile.updatedAt}`;
  let request = sharedCopyRequests.get(requestKey);
  if (!request) {
    request = createShareToken()
      .then(async (token) => {
        await saveSharedProfile(fetchRequest, token, createProfileShare(profile, allProfileSections));
        return sharedProfileLink(token, "share");
      })
      .finally(() => sharedCopyRequests.delete(requestKey));
    sharedCopyRequests.set(requestKey, request);
  }
  return request;
}

function cachedSharedCopyLink(profile) {
  const cachedShare = readPresentationState(sharedCopyStateOptions(profile.id), null, validCachedShare);
  return cachedShare?.profileUpdatedAt === profile.updatedAt
    && Date.now() - Date.parse(cachedShare.createdAt) < sharedCopyLifetime
    ? cachedShare.link
    : null;
}

function sharedCopyStateOptions(profileId) {
  return { lifetime: "session", name: "shared-copy", page: "profiles", profileId, scope: "profile" };
}

function profileIdAfterDeletion(application, deletedProfileId) {
  return application.profile.id === deletedProfileId
    ? application.profiles.find((profile) => profile.id !== deletedProfileId)?.id
    : application.profile.id;
}

function profileManagerPageError(error, dialogIds) {
  return dialogIds.some(Boolean) ? null : error;
}

function ActiveProfile({ application, onClear, onCopy, onDelete, onExport, onRename, onShare, onSync, profile }) {
  const summary = profileShareSummary({ profile: { data: profile.data } });
  const token = sharedProfileToken(profile.id);
  const lastSavedAt = token ? sharedProfileLastSavedAt(profile.id) : null;
  return <section className="active-profile" aria-labelledby="active-profile-heading">
    <div className="active-profile-details">
      <Heading as="h2" className="active-profile-title" id="active-profile-heading"><ProfileIdentity profile={profile} /><Label>Active</Label></Heading>
      <Text>{profileSummaryText(summary)}</Text>
      {token ? <Text>Server sync: On{lastSavedAt ? ` · Last saved ${relativeTime(lastSavedAt)}` : ""}</Text> : null}
    </div>
    <div className="active-profile-actions"><ActiveProfileActions canCopy={application.profiles.length > 1} canDelete={application.profiles.length > 1} onClear={onClear} onCopy={onCopy} onDelete={onDelete} onExport={onExport} onRename={onRename} onShare={onShare} onSync={onSync} profile={profile} /></div>
  </section>;
}

function RenameProfileDialog({ application, error, name, onChange, onClose, onRename, profile, returnFocusRef }) {
  const inputRef = useRef(null);
  if (!profile) return null;
  const unchanged = name.trim() === profile.name;
  return <Dialog className="profile-rename-dialog" title={`Rename ${profile.name}`} initialFocusRef={inputRef} returnFocusRef={returnFocusRef} onClose={onClose}>
    <form onSubmit={(event) => { event.preventDefault(); if (!application.saving && name.trim() && !unchanged) onRename(); }}>
      {error ? <Alert tone="danger" heading="Unable to rename profile">{error}</Alert> : null}
      <FormControl required><FormControl.Label>Profile name</FormControl.Label><TextInput ref={inputRef} value={name} onChange={(event) => onChange(event.target.value)} block /></FormControl>
    </form>
    <Dialog.Footer><Button tone="secondary" onClick={onClose}>Cancel</Button><Button labelDetail={application.saving ? undefined : "profile"} disabled={application.saving || !name.trim() || unchanged} onClick={onRename}>{application.saving ? "Renaming…" : "Rename"}</Button></Dialog.Footer>
  </Dialog>;
}

function DestructiveProfileDialog({ action, application, error, onClose, onConfirm, profile, returnFocusRef }) {
  if (!profile) return null;
  const deleting = action === "delete";
  const nextProfile = deleting && application.profile.id === profile.id
    ? application.profiles.find((candidate) => candidate.id !== profile.id)
    : null;
  const title = deleting ? `Delete ${profile.name}?` : `Clear data from ${profile.name}?`;
  const subtitle = deleting
    ? "This will permanently delete the profile and all its data."
    : "This will remove all portfolio and configuration data stored in this profile.";
  const buttonLabel = deleting ? "Delete" : "Clear data";
  const warning = deleting
    ? `Any Server Sync connection for this profile saved on this device will also be forgotten.${nextProfile ? ` ${nextProfile.name} will become active.` : ""}`
    : `The ${profile.name} profile will remain, but its properties, assets, allocation, policies, and Browser views will be deleted.`;
  return <Dialog className="profile-destructive-dialog" title={title} subtitle={subtitle} onClose={onClose} returnFocusRef={returnFocusRef}>
    {error ? <Alert tone="danger" heading={`Unable to ${deleting ? "delete" : "clear"} profile`}>{error}</Alert> : null}
    <Alert tone="warning" heading="This cannot be undone">{warning}</Alert>
    <Dialog.Footer><Button tone="secondary" onClick={onClose}>Cancel</Button><Button tone="danger" labelDetail={deleting && !application.saving ? "profile" : undefined} disabled={application.saving} onClick={onConfirm}>{application.saving ? `${deleting ? "Deleting" : "Clearing"}…` : buttonLabel}</Button></Dialog.Footer>
  </Dialog>;
}

function ActiveProfileActions({ canCopy, canDelete, onClear, onCopy, onDelete, onExport, onRename, onShare, onSync, profile }) {
  return <ActionMenu><ActionMenu.Button id={profileActionsId(profile.id)} aria-label={`Actions for ${profile.name}`}>Actions</ActionMenu.Button><ActionMenu.Overlay><ActionList>
    <ActionList.Item className="rename-profile" onSelect={onRename}><ActionList.LeadingVisual><PencilIcon /></ActionList.LeadingVisual>Rename</ActionList.Item>
    <ActionList.Divider />
    <ActionList.Item className="export-profile" onSelect={onExport}><ActionList.LeadingVisual><DownloadIcon /></ActionList.LeadingVisual>Export</ActionList.Item>
    <ActionList.Item className="share-profile" onSelect={onShare}><ActionList.LeadingVisual><ShareIcon /></ActionList.LeadingVisual>Share a copy…</ActionList.Item>
    <ActionList.Item className="sync-profile" onSelect={onSync}><ActionList.LeadingVisual><SyncIcon /></ActionList.LeadingVisual>Server sync…</ActionList.Item>
    <ActionList.Divider />
    <ActionList.Item className="copy-profile" disabled={!canCopy} onSelect={onCopy}><ActionList.LeadingVisual><CopyIcon /></ActionList.LeadingVisual>Copy sections from…{!canCopy ? <ActionList.Description variant="block">No other profiles available</ActionList.Description> : null}</ActionList.Item>
    <ActionList.Divider />
    <ActionList.Item className="clear-profile" onSelect={onClear}><ActionList.LeadingVisual><XCircleIcon /></ActionList.LeadingVisual>Clear data</ActionList.Item>
    {canDelete ? <ActionList.Item className="delete-profile" variant="danger" onSelect={onDelete}><ActionList.LeadingVisual><TrashIcon /></ActionList.LeadingVisual>Delete profile</ActionList.Item> : null}
  </ActionList></ActionMenu.Overlay></ActionMenu>;
}

function InactiveProfileActions({ onDelete, onRename, profile }) {
  return <ActionMenu><ActionMenu.Button id={profileActionsId(profile.id)} aria-label={`Actions for ${profile.name}`}>Actions</ActionMenu.Button><ActionMenu.Overlay><ActionList>
    <ActionList.Item className="rename-profile" onSelect={onRename}><ActionList.LeadingVisual><PencilIcon /></ActionList.LeadingVisual>Rename</ActionList.Item>
    <ActionList.Divider />
    <ActionList.Item className="delete-profile" variant="danger" onSelect={onDelete}><ActionList.LeadingVisual><TrashIcon /></ActionList.LeadingVisual>Delete profile</ActionList.Item>
  </ActionList></ActionMenu.Overlay></ActionMenu>;
}

function profileActionsId(profileId) {
  return `profile-actions-${profileId}`;
}

function profileSummaryText(summary) {
  return [countLabel(summary.assets, "asset"), countLabel(summary.properties, "property", "properties"), countLabel(summary.policies, "policy", "policies"), countLabel(summary.browserViews, "view")].join(" · ");
}

function countLabel(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function relativeTime(value, now = Date.now()) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "recently";
  const elapsed = Math.max(0, now - timestamp);
  if (elapsed < 60_000) return "just now";
  if (elapsed < 3_600_000) return `${countLabel(Math.floor(elapsed / 60_000), "minute")} ago`;
  if (elapsed < 86_400_000) return `${countLabel(Math.floor(elapsed / 3_600_000), "hour")} ago`;
  return `${countLabel(Math.floor(elapsed / 86_400_000), "day")} ago`;
}

function CopyProfileSectionsDialog({ application, confirming, error, onBack, onChangeSection, onChangeSource, onClose, onConfirm, onReview, sections, sourceId, target }) {
  const source = application.profiles.find((profile) => profile.id === sourceId);
  const selectedSections = selectedSectionNames(sections);
  const policyChanges = confirming && source && sections.keepLocalPolicySettings
    ? prepareProfileSectionsCopy(target.data, source.data, sections).policyChanges : null;
  if (confirming && source) return <Dialog className="profile-share-dialog" title={`Update ${target.name}?`} subtitle={`Copy selected sections from ${source.name}.`} onClose={onClose} footerButtons={[{ content: "Back", onClick: onBack }, { content: application.saving ? "Updating…" : "Update profile", buttonType: "danger", onClick: onConfirm, disabled: application.saving }]}>
    <div className="profile-import-confirmation">
      {error ? <Alert tone="danger" heading="Unable to copy profile sections">{error}</Alert> : null}
      <Alert tone="warning" heading={`Selected sections in ${target.name} will be updated`}>{policyChanges ? "Configuration will be copied with matching local policy settings retained. Unselected sections will remain unchanged." : "This does not create a live link or merge records. Unselected sections will remain unchanged."}</Alert>
      <Text>Copy {selectedSections.join(", ")} from {source.name} into {target.name}.</Text>
      {policyChanges ? <div className="profile-policy-copy-summary">
        <Text>{policyChanges.added.length} added · {policyChanges.retained.length} kept with local settings · {policyChanges.removed.length} removed</Text>
        {policyChanges.removed.length ? <Text>Cannot keep: {policyChanges.removed.map((policy) => policyDisplayTitle(target.data, policy).label).join(", ")} (their properties or values are not in the copied Configuration).</Text> : null}
      </div> : null}
    </div>
  </Dialog>;
  const summary = source ? profileShareSummary({ profile: { data: source.data } }) : null;
  return <Dialog className="profile-share-dialog" title={`Copy sections into ${target.name}`} subtitle="Reuse data from another profile without linking them." onClose={onClose} footerButtons={[{ content: "Cancel", onClick: onClose }, { content: "Review copy", buttonType: "primary", onClick: onReview, disabled: application.saving || !source || !selectedSections.length }]}>
    <div className="profile-import-preview">
      {error ? <Alert tone="danger" heading="Unable to copy profile sections">{error}</Alert> : null}
      <FormControl><FormControl.Label>Copy from</FormControl.Label><Select value={sourceId} onChange={(event) => onChangeSource(event.target.value)} block>{application.profiles.filter((profile) => profile.id !== target.id).map((profile) => <Select.Option value={profile.id} key={profile.id}>{profile.name}</Select.Option>)}</Select></FormControl>
      {summary ? <ProfileSummary summary={summary} /> : null}
      <fieldset className="profile-section-choices profile-copy-options"><legend>Copy options</legend>
        <FormControl layout="horizontal" disabled={sections.allocation || sections.browserViews}><Checkbox checked={sections.configuration} onChange={(event) => onChangeSection("configuration", event.target.checked)} /><FormControl.Label>Copy Configuration</FormControl.Label><FormControl.Caption>Properties, assets, assignments, and policies</FormControl.Caption></FormControl>
        <FormControl className="profile-policy-copy-option" layout="horizontal" disabled={!sections.configuration}><Checkbox checked={sections.keepLocalPolicySettings} onChange={(event) => onChangeSection("keepLocalPolicySettings", event.target.checked)} /><FormControl.Label>Keep local policy settings</FormControl.Label><FormControl.Caption>Preserve limits and enabled states for matching policies.</FormControl.Caption></FormControl>
        <FormControl layout="horizontal"><Checkbox checked={sections.browserViews} onChange={(event) => onChangeSection("browserViews", event.target.checked)} /><FormControl.Label>Copy Browser views</FormControl.Label></FormControl>
        <FormControl layout="horizontal"><Checkbox checked={sections.allocation} onChange={(event) => onChangeSection("allocation", event.target.checked)} /><FormControl.Label>Copy Allocation</FormControl.Label></FormControl>
      </fieldset>
    </div>
  </Dialog>;
}

function ExportProfileDialog({ error, onChange, onClose, onExport, profile, sections }) {
  return <Dialog className="profile-share-dialog" title={`Export ${profile.name}`} subtitle="Choose what to include in the shared profile file." onClose={onClose} footerButtons={[{ content: "Cancel", onClick: onClose }, { content: "Download", buttonType: "primary", onClick: onExport }]}>
    {error ? <Alert tone="danger" heading="Unable to export profile">{error}</Alert> : null}
    <SectionChoices legend="Export sections" sections={sections} onChange={onChange} />
  </Dialog>;
}

function ImportProfileDialog({ application, confirmingReplace, error, name, onBack, onChangeName, onChangeSection, onChangeTarget, onClose, onImport, onReplace, sections, share, targetId }) {
  const summary = profileShareSummary(share);
  const target = application.profiles.find((profile) => profile.id === targetId);
  const selectedSections = selectedSectionNames(sections);
  if (confirmingReplace && target) return <Dialog className="profile-share-dialog" title={`Replace ${target.name}?`} subtitle="Review this destructive profile update." onClose={onClose} footerButtons={[{ content: "Back", onClick: onBack }, { content: application.saving ? "Replacing…" : "Replace profile", buttonType: "danger", onClick: onReplace, disabled: application.saving }]}>
    <div className="profile-import-confirmation">
      {error ? <Alert tone="danger" heading="Unable to import profile">{error}</Alert> : null}
      <Alert tone="warning" heading={`Selected sections in ${target.name} will be replaced`}>This does not merge records and cannot be undone. Unselected sections will remain unchanged.</Alert>
      <Text>Replace {selectedSections.join(", ")} using the data shared from {share.profile.name}.</Text>
    </div>
  </Dialog>;
  return <Dialog className="profile-share-dialog" title="Import profile" subtitle={`Shared from ${share.profile.name}`} onClose={onClose} footerButtons={[{ content: "Cancel", onClick: onClose }, { content: target ? "Review replacement" : "Import as new profile", buttonType: "primary", onClick: onImport, disabled: application.saving || !selectedSections.length }]}>
    <div className="profile-import-preview">
      {error ? <Alert tone="danger" heading="Unable to import profile">{error}</Alert> : null}
      <FormControl><FormControl.Label>Destination</FormControl.Label><Select value={targetId} onChange={(event) => onChangeTarget(event.target.value)} block><Select.Option value="">Create new profile</Select.Option>{application.profiles.map((profile) => <Select.Option value={profile.id} key={profile.id}>Replace {profile.name}</Select.Option>)}</Select><FormControl.Caption>{target ? `Selected sections in ${target.name} will be replaced after confirmation.` : "The imported data will remain isolated from existing profiles."}</FormControl.Caption></FormControl>
      {!target ? <FormControl required><FormControl.Label>New profile name</FormControl.Label><TextInput value={name} onChange={(event) => onChangeName(event.target.value)} block /></FormControl> : null}
      <ProfileSummary summary={summary} />
      <SectionChoices legend="Import sections" sections={sections} available={share.profile.sections} onChange={onChangeSection} />
    </div>
  </Dialog>;
}

function ProfileSummary({ summary }) {
  return <dl className="profile-import-summary"><div><dt>Properties</dt><dd>{summary.properties}</dd></div><div><dt>Assets</dt><dd>{summary.assets}</dd></div><div><dt>Policies</dt><dd>{summary.policies}</dd></div><div><dt>Allocations</dt><dd>{summary.allocations}</dd></div><div><dt>Browser views</dt><dd>{summary.browserViews}</dd></div></dl>;
}

function selectedSectionNames(sections) {
  return [
    sections.configuration ? "Configuration" : null,
    sections.browserViews ? "Browser views" : null,
    sections.allocation ? "Allocation" : null
  ].filter(Boolean);
}

function SectionChoices({ available = { configuration: true, allocation: true, browserViews: true }, legend, onChange, sections }) {
  return <fieldset className="profile-section-choices"><legend>{legend}</legend>
    <FormControl layout="horizontal" disabled={sections.allocation || sections.browserViews}><Checkbox checked={sections.configuration} onChange={(event) => onChange("configuration", event.target.checked)} /><FormControl.Label>Configuration</FormControl.Label><FormControl.Caption>Properties, assets, assignments, and policies</FormControl.Caption></FormControl>
    {available.browserViews ? <FormControl layout="horizontal"><Checkbox checked={sections.browserViews} onChange={(event) => onChange("browserViews", event.target.checked)} /><FormControl.Label>Browser views</FormControl.Label></FormControl> : null}
    {available.allocation ? <FormControl layout="horizontal"><Checkbox checked={sections.allocation} onChange={(event) => onChange("allocation", event.target.checked)} /><FormControl.Label>Allocation</FormControl.Label></FormControl> : null}
  </fieldset>;
}

function changeSectionSelection(current, section, checked) {
  const next = { ...current, [section]: checked };
  if (section === "keepLocalPolicySettings") return next;
  if (checked && section !== "configuration") next.configuration = true;
  return next;
}

function downloadProfileShare(share) {
  const safeName = share.profile.name.trim().toLocaleLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "profile";
  const url = URL.createObjectURL(new Blob([`${JSON.stringify(share, null, 2)}\n`], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `regulator-profile-${safeName}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

function profileNameError(profiles, value, excludedProfileId) {
  const name = value.trim();
  if (!name) return "Enter a profile name.";
  if (profiles.some((profile) => profile.id !== excludedProfileId && profile.name.toLocaleLowerCase() === name.toLocaleLowerCase())) return "Profile names must be unique.";
  return null;
}
